/**
 * Serializes a filter-state record into an API query object using each
 * FilterConfig's `toApiParam` override.
 *
 * Rules (per config):
 * - If a filter's value is "empty" (null/undefined/''/false/[]/range-with-missing-end),
 *   it is omitted entirely from the output.
 * - Otherwise: the param name is `toApiParam.name ?? config.id`, and the
 *   value runs through `toApiParam.transform` if present, else is used as-is.
 *
 * Additional query fields (pagination, sorting, etc.) can be provided via
 * `extras` and are merged on top of serialized filter params.
 *
 * Usage:
 * ```ts
 * const query = buildQuery(FILTERS, values, { page: 1, per_page: 25 });
 * fetchAccessorials(query);
 * ```
 */
export function buildQuery(configs, values, extras = {}) {
    var _a, _b, _c;
    const out = {};
    for (const cfg of configs) {
        if (cfg.featureFlag && !cfg.featureFlag())
            continue;
        const raw = values[cfg.id];
        if (isEmpty(raw, cfg.kind))
            continue;
        const name = (_b = (_a = cfg.toApiParam) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : cfg.id;
        const serialized = ((_c = cfg.toApiParam) === null || _c === void 0 ? void 0 : _c.transform)
            ? cfg.toApiParam.transform(raw)
            : raw;
        if (serialized === undefined)
            continue;
        out[name] = serialized;
    }
    return Object.assign(Object.assign({}, out), extras);
}
function isEmpty(v, kind) {
    if (v == null)
        return true;
    if (v === '')
        return true;
    if (v === false)
        return true;
    if (Array.isArray(v) && v.length === 0)
        return true;
    if (kind === 'date-range') {
        const r = v;
        return r.from == null || r.from === '' || r.to == null || r.to === '';
    }
    return false;
}
